import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot,
} from "@angular/router";
import { BehaviorSubject, of, Subject } from "rxjs";
import { catchError, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { Product } from "src/app/model/price/product";
import { CreateRequest } from "src/app/model/savings-plan/create-request";
import { SavingPlan } from "src/app/model/savings-plan/saving-plan";
import { ServerType } from "src/service/app-init.service";
import { RestApiService } from "src/service/rest-api.service";
import { UserService } from "src/service/user.service";
import { PriceService } from "../price/price.service";

@Injectable({
  providedIn: "root",
})
export class SavingPlansService implements Resolve<any> {
  constructor(
    private restApiService: RestApiService,
    private priceService: PriceService,
    private userService: UserService
  ) {}

  $destroy = new Subject();

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.refreshSavingPlans();
  }

  myPackages = new BehaviorSubject<SavingPlan[]>([]);

  selectedSavingPlan = new BehaviorSubject<Product>(null);

  createRequest = new BehaviorSubject<CreateRequest>(null);

  endpoint = "savings/";

  tresorGold = {
    name: "Tresor Gold",
    description: "Tresor Gold",
    type: "TRESOR",
  };

  refreshSavingPlans() {
    return this.getSavingPlans().pipe(
      takeUntil(this.$destroy),
      tap((myPackages) => {
        this.myPackages.next(myPackages);
        this.$destroy.next();
      }),
      catchError((error) => {
        this.$destroy.next();
        return of(false);
      })
    );
  }

  refreshSavingPlansWithoutTakeUntil() {
    return this.getSavingPlans().pipe(
      tap((myPackages) => this.myPackages.next(myPackages)),
      catchError((error) => of(false))
    );
  }

  getSavingPlansProducts() {
    return this.priceService.publishedProductList.pipe(
      map((productList?) => [this.tresorGold, ...productList?.filter(product=>product?.publish)])
    );
  }

  create(createRequest) {
    return this.restApiService.post(
      ServerType.WALLET,
      this.endpoint + "new",
      createRequest
    );
  }

  createTresorGoldPlan(createRequest) {
    return this.restApiService.post(
      ServerType.WALLET,
      this.endpoint + "new-tresorgold",
      createRequest
    );
  }

  sell(sellRequest, tresorgold: boolean) {
    return this.restApiService.post(
      ServerType.WALLET,
      `${this.endpoint}${tresorgold ? "sell-tresorgold" : "cancel"}`,
      { ...sellRequest, priceJwt: this.priceService.priceJwt.value }
    );
    //.pipe(switchMap(()=>this.refreshSavingPlans()));
  }

  getSavingPlans() {
    return this.restApiService.get(ServerType.WALLET, this.endpoint + "all");
  }

  getSavingPlanShipmentCost(savingsPlanId, afterPurchaseType) {
    return this.restApiService.post(
      ServerType.WALLET,
      this.endpoint + "shipment-cost",
      {
        country: this.userService.user.value.country,
        priceJwt: this.priceService.priceJwt.value,
        savingsPlanId,
        afterPurchaseType,
      }
    );
  }

  buyForGoldCard(
    amount: number,
    paymentType: string,
    savingsPlanId: string,
    stripeTokenId?: string
  ) {
    return this.restApiService.post(
      ServerType.WALLET,
      this.endpoint + "buy-for-goldcard",
      {
        amount: amount,
        paymentType: paymentType,
        savingsPlanId,
        priceJwt: this.priceService.priceJwt.value,
        stripeTokenId,
      }
    );
  }

  transferToWallet(amount: number, savingsPlanId: string) {
    return this.restApiService.post(
      ServerType.WALLET,
      this.endpoint + "sell-for-goldcard",
      { amount, savingsPlanId, priceJwt: this.priceService.priceJwt.value }
    );
  }

  cancelSavingPlan(amount: number, savingsPlanId: string) {
    return this.restApiService.post(
      ServerType.WALLET,
      this.endpoint + "cancel",
      { amount, savingsPlanId, priceJwt: this.priceService.priceJwt.value }
    );
  }

  updateName(newName: string, planId: string) {
    return this.restApiService
      .post(ServerType.WALLET, this.endpoint + "update-name", {
        newName,
        planId,
      })
      .pipe(switchMap(() => this.refreshSavingPlans()));
  }

  getSavingsPlanById(id: string) {
    return this.restApiService.get(ServerType.WALLET, `${this.endpoint}${id}`);
  }

  withdraw(savingPlanId: string, witdrawRequest) {
    return this.restApiService.post(
      ServerType.WALLET,
      this.endpoint + "withdraw",
      {
        ...witdrawRequest,
        savingPlanId,
        priceJwt: this.priceService.priceJwt.value,
      }
    );
  }
}
